import { Box, Button, Flex, Input, InputGroup, InputLeftElement } from '@chakra-ui/react';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { TOOLBAR_COMPONENT_HEIGHT } from 'constants/editor';
import { Code } from 'constants/keyboard';
import { createContext, useContext, useRef } from 'react';

import {
  BRAND_FONT_LIST_ID,
  BRAND_FONT_OPTION_PREFIX,
  DOCUMENT_FONT_LIST_ID,
  DOCUMENT_FONT_OPTION_PREFIX,
  OPTION_CONTAINER_ID,
} from './SelectList.config';
import { Option, SelectMenuListProps, SelectMenuProps, SelectMenuSearchProps } from './SelectList.types';

export const SelectMenuList = ({ children, label, testId, ...otherProps }: SelectMenuListProps) => {
  const { onClose, value, optionRefList, brandOptionRefList, documentOptionRefList, options, onChange } =
    useContext(SelectContext);

  const optionsWrapperRef = useRef<HTMLDivElement>(null);

  // Handles the expected listbox keyboard shortcuts
  const onKeyDown = (e: React.KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (!target) return;

    const id = target.id;
    if (!id) return;

    const optionEl = optionRefList?.[id];
    const brandOptionEl = brandOptionRefList?.[id];
    const documentOptionEl = documentOptionRefList?.[id];
    const isOptionTarget = !!optionEl;
    const isBrandOptionTarget = !!brandOptionEl;
    const isDocumentOptionTarget = !!documentOptionEl;
    const isListBoxTarget = id === OPTION_CONTAINER_ID;
    const brandFontListEl = document.getElementById(BRAND_FONT_LIST_ID);
    const documentFontListEl = document.getElementById(DOCUMENT_FONT_LIST_ID);

    // focus next the next option in the list
    if (e.key === Code.ArrowDown) {
      e.preventDefault();

      if (!isOptionTarget && !isBrandOptionTarget && !isDocumentOptionTarget) return;

      if (isBrandOptionTarget) {
        const next = brandOptionEl.nextElementSibling as HTMLDivElement;
        if (next) return next.focus();

        if (documentFontListEl && documentFontListEl.firstElementChild) {
          return (documentFontListEl.firstElementChild as HTMLElement).focus();
        }

        return (optionRefList[options[0].value] as HTMLElement).focus();
      }

      if (isDocumentOptionTarget) {
        const next = documentOptionRefList[id]?.nextElementSibling as HTMLDivElement;
        if (next) return next.focus();
        return (optionRefList[options[0].value] as HTMLElement).focus();
      }

      const next = optionRefList[id].nextElementSibling as HTMLDivElement;
      if (next) next.focus();
    }

    // focus next the previous option in the list
    if (e.key === Code.ArrowUp) {
      e.preventDefault();
      if (!isOptionTarget && !isBrandOptionTarget && !isDocumentOptionTarget) return;

      if (isBrandOptionTarget) {
        const prev = brandOptionEl.previousElementSibling as HTMLDivElement;
        if (prev) return prev.focus();
      }

      if (isDocumentOptionTarget) {
        const prev = documentOptionRefList[id].previousElementSibling as HTMLDivElement;
        if (prev) return prev.focus();

        // If it is the first document font, focus the last brand font
        if (brandFontListEl) (brandFontListEl.lastElementChild as HTMLElement)?.focus();

        return;
      }

      const prev = optionRefList[id].previousElementSibling as HTMLDivElement;
      if (prev) return prev.focus();

      // focus last document font if going up from the first option, if no document fonts, focus the last brand font
      if (documentFontListEl) return (documentFontListEl.lastElementChild as HTMLElement)?.focus();
      if (brandFontListEl) (brandFontListEl.lastElementChild as HTMLElement)?.focus();
    }

    // focus between the list components
    if (e.key === Code.Tab) {
      if (e.shiftKey) {
        if (isListBoxTarget) return;

        // set focus back to document fonts if shift tab is pressed in the all fonts list
        if (isOptionTarget) {
          e.preventDefault();
          if (documentFontListEl && documentFontListEl.firstElementChild)
            return (documentFontListEl.firstElementChild as HTMLElement).focus();
          if (brandFontListEl && brandFontListEl.firstElementChild)
            return (brandFontListEl.firstElementChild as HTMLElement).focus();
          return optionsWrapperRef?.current?.focus();
        }

        if (isDocumentOptionTarget) {
          e.preventDefault();

          if (brandFontListEl) return (brandFontListEl.firstElementChild as HTMLElement)?.focus();

          return optionsWrapperRef?.current?.focus();
        }

        if (isBrandOptionTarget) {
          e.preventDefault();
          return optionsWrapperRef?.current?.focus();
        }
      }

      if (isListBoxTarget) {
        e.preventDefault();
        if (brandFontListEl) return (brandFontListEl.firstElementChild as HTMLElement)?.focus();
        if (documentFontListEl) return (documentFontListEl.firstElementChild as HTMLElement)?.focus();
      }

      // set focus to document fonts if tab is pressed on brand font
      if (brandOptionEl) {
        e.preventDefault();

        // Focus the first document font if it exists
        if (documentFontListEl && documentFontListEl.firstElementChild) {
          return (documentFontListEl.firstElementChild as HTMLElement).focus();
        }

        // If there is no document font, focus the first option in all fonts
        return (optionRefList[options[0].value] as HTMLElement).focus();
      }

      // set focus to all fonts if tab is pressed on document font
      if (documentOptionEl) {
        e.preventDefault();
        return (optionRefList[options[0].value] as HTMLElement).focus();
      }

      if (!isListBoxTarget) return e.preventDefault();

      const selectedOption = options.find((option: Option) => option.value === value);
      if (selectedOption || !options.length) return;

      e.preventDefault();

      if (brandFontListEl) return (brandFontListEl.firstElementChild as HTMLElement)?.focus();
      if (documentFontListEl) (documentFontListEl.firstElementChild as HTMLElement)?.focus();
      return (optionRefList[options[0].value] as HTMLElement).focus();
    }

    // focus the list box element if option
    // else close the dropdown
    if (e.key === Code.Escape) {
      if (isListBoxTarget) return onClose();

      if (isOptionTarget || isBrandOptionTarget || documentOptionEl) {
        e.preventDefault();
        return optionsWrapperRef?.current?.focus();
      }
    }

    // select the focused option option
    if (e.key === Code.Enter || e.key === Code.Space) {
      if (isListBoxTarget) return;

      if (isDocumentOptionTarget) {
        e.preventDefault();
        const documentOptionValue = id.replace(DOCUMENT_FONT_OPTION_PREFIX, '');
        if (value === documentOptionValue) return;

        onChange(documentOptionValue);
        return onClose();
      }

      if (isBrandOptionTarget) {
        e.preventDefault();
        const brandOptionValue = id.replace(BRAND_FONT_OPTION_PREFIX, '');
        if (value === brandOptionValue) return;

        onChange(brandOptionValue);
        return onClose();
      }

      if (isOptionTarget && value !== id) {
        e.preventDefault();
        onChange(id);
        return onClose();
      }
    }
  };

  return (
    <Flex
      direction='column'
      gap={1}
      pt={0}
      maxH='284px'
      overflowY='auto'
      role='listbox'
      tabIndex={0}
      ref={optionsWrapperRef}
      aria-labelledby={label}
      onKeyDown={onKeyDown}
      id={OPTION_CONTAINER_ID}
      data-testid={testId}
      sx={{ scrollbarGutter: 'stable' }}
      {...otherProps}
    >
      {children}
    </Flex>
  );
};

type SelectMenuOptionProps = {
  option: Option;
  isSelected: boolean;
  isDocumentFont?: boolean;
  isBrandFont?: boolean;
  children: React.ReactNode;
};

export const SelectMenuOption = ({
  option,
  isSelected,
  isBrandFont = false,
  isDocumentFont = false,
  children,
}: SelectMenuOptionProps) => {
  const { setOptionRef, setBrandOptionRef, setDocumentOptionRef, onChange } = useContext(SelectContext);
  const { value } = option;
  const onClick = () => onChange(value);

  const setRef = (ref: Element | null) => {
    if (isDocumentFont) setDocumentOptionRef(ref, `${DOCUMENT_FONT_OPTION_PREFIX}${value}`);
    else if (isBrandFont) setBrandOptionRef(ref, `${BRAND_FONT_OPTION_PREFIX}${value}`);
    else setOptionRef(ref, value);
  };

  // roving tabindex with the selected option
  const tabIndex = isSelected ? 0 : -1;
  const id = (() => {
    if (isDocumentFont) return `${DOCUMENT_FONT_OPTION_PREFIX}${value}`;
    if (isBrandFont) return `${BRAND_FONT_OPTION_PREFIX}${value}`;
    return value;
  })();

  return (
    <Button
      ref={setRef}
      id={id}
      data-testid={id}
      onClick={onClick}
      isActive={isSelected}
      aria-selected={isSelected}
      tabIndex={tabIndex}
      as='div'
      variant='toolbar-dropdown-item'
      role='option'
      size='xs'
      minH={TOOLBAR_COMPONENT_HEIGHT}
      maxH={TOOLBAR_COMPONENT_HEIGHT}
      cursor='pointer'
    >
      {children}
    </Button>
  );
};

export const SelectMenuSearch = ({
  onFilter,
  unfilteredOptions,
  setIsSearching,
  isSearching,
}: SelectMenuSearchProps) => {
  const { onClose } = useContext(SelectContext);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === Code.Escape) return onClose();
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const search = e.target.value?.toLowerCase();
    if (!search.length) setIsSearching(false);
    if (search.length && !isSearching) setIsSearching(true);

    const filteredOptionList = unfilteredOptions.filter(
      (option) => !search || option.value.toLowerCase().indexOf(search) !== -1,
    );

    onFilter(filteredOptionList);
  };

  return (
    <Box height={8} margin={3} marginBottom={1}>
      <InputGroup size='xs' height='full'>
        <InputLeftElement pointerEvents='none' height='full'>
          <SearchIcon width='16px' height='16px' stroke='var(--vg-colors-upgrade-blue-500' />
        </InputLeftElement>
        <Input
          onKeyDown={onKeyDown}
          placeholder='Search'
          onChange={onChange}
          height={8}
          size={'sm'}
          borderColor={'gray.500'}
          borderRadius={'base'}
          _placeholder={{ color: 'font.300' }}
        />
      </InputGroup>
    </Box>
  );
};

// shared internal state for SelectMenu components
const SelectContext = createContext({
  setOptionRef: (ref: Element | null, optionValue: string) => {},
  setBrandOptionRef: (ref: Element | null, optionValue: string) => {},
  setDocumentOptionRef: (ref: Element | null, optionValue: string) => {},
  onChange: (value: string) => {},
  onClose: () => {},
  optionRefList: {} as { [key: string]: Element },
  brandOptionRefList: {} as { [key: string]: Element },
  documentOptionRefList: {} as { [key: string]: Element },
  options: [] as Option[],
  value: '',
});

export const SelectMenu = ({ children, onChange, value, options, onClose }: SelectMenuProps) => {
  const optionRefList = useRef<{ [value: string]: HTMLDivElement }>({});
  const brandOptionRefList = useRef<{ [value: string]: HTMLDivElement }>({});
  const documentOptionRefList = useRef<{ [value: string]: HTMLDivElement }>({});

  const setOptionRef = (ref: Element | null, optionValue: string) => {
    if (!ref) return null;
    optionRefList.current[optionValue] = ref as HTMLDivElement;
  };

  const setBrandOptionRef = (ref: Element | null, optionValue: string) => {
    if (!ref) return null;
    brandOptionRefList.current[optionValue] = ref as HTMLDivElement;
  };

  const setDocumentOptionRef = (ref: Element | null, optionValue: string) => {
    if (!ref) return null;
    documentOptionRefList.current[optionValue] = ref as HTMLDivElement;
  };

  return (
    <Flex direction='column' height='full'>
      <SelectContext.Provider
        value={{
          onChange,
          setOptionRef,
          setBrandOptionRef,
          setDocumentOptionRef,
          optionRefList: optionRefList.current,
          brandOptionRefList: brandOptionRefList.current,
          documentOptionRefList: documentOptionRefList.current,
          value,
          options,
          onClose,
        }}
      >
        {children}
      </SelectContext.Provider>
    </Flex>
  );
};
